const MB = 1024 * 1024;
/** Largest accepted upload per content type. Every allowlisted type has an entry. */
const MAX_SIZE_BYTES_BY_CONTENT_TYPE = {
    'text/html': 10 * MB,
    'text/plain': 10 * MB,
    'text/csv': 10 * MB,
    'text/markdown': 10 * MB,
    'text/x-markdown': 10 * MB,
    'image/jpeg': 20 * MB,
    'image/png': 20 * MB,
    'image/gif': 20 * MB,
    'image/webp': 20 * MB,
    'image/svg+xml': 20 * MB,
    'image/avif': 20 * MB,
    'image/heic': 20 * MB,
    'image/heif': 20 * MB,
    'application/json': 10 * MB,
    'application/yaml': 10 * MB,
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 50 * MB,
    'application/pdf': 50 * MB,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 50 * MB,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 50 * MB,
    'application/zip': 100 * MB,
    'video/mp4': 200 * MB,
    'video/webm': 200 * MB,
    'video/quicktime': 200 * MB,
    // Compressed audio is small; a WAV is roughly 10 MB a minute, so it gets twice the room.
    'audio/mpeg': 50 * MB,
    'audio/mp4': 50 * MB,
    'audio/x-m4a': 50 * MB,
    'audio/wav': 100 * MB,
    'audio/x-wav': 100 * MB,
};
const MAX_PRESIGN_TTL_SECONDS = 24 * 60 * 60;
export const UPLOAD_POLICY = {
    /** The largest per-type cap: the ceiling for any upload. Use `maxSizeBytesFor` for a given type. */
    maxFileSizeBytes: Math.max(...Object.values(MAX_SIZE_BYTES_BY_CONTENT_TYPE)),
    /** Uploads one user may start per rolling minute. */
    maxUploadsPerUserPerMinute: 60,
    /** Uploads one account may start per rolling minute. */
    maxUploadsPerAccountPerMinute: 600,
    uploadRateWindowSeconds: 60,
    maxAttachmentsPerMessage: 10,
    maxFileNameLength: 255,
    maxContentTypeLength: 128,
    partSizeBytes: 8 * 1024 * 1024,
    presignTtlSeconds: 15 * 60,
    allowedContentTypes: [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/svg+xml',
        'image/avif',
        'image/heic',
        'image/heif',
        'video/mp4',
        'video/webm',
        'video/quicktime',
        'audio/mpeg',
        'audio/wav',
        'audio/x-wav',
        'audio/mp4',
        'audio/x-m4a',
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/json',
        'application/yaml',
        'text/csv',
        'text/plain',
        'text/markdown',
        'text/x-markdown',
        'text/html',
        'application/zip',
    ],
};
/**
 * Archive, Office and PDF types stay unviewable and un-downloadable until a malware scan comes
 * back clean. They can carry payloads that run in a viewer or on the reader's machine; HTML,
 * text, images and video are rendered in the browser sandbox or by the browser's own decoders.
 */
export const SCAN_GATED_CONTENT_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/zip',
];
/** True for types that need a clean scan before anyone can open them. */
export const isScanGatedContentType = (contentType) => SCAN_GATED_CONTENT_TYPES.includes(contentType.split(';', 1)[0]?.trim().toLowerCase() ?? '');
/**
 * Why the bytes of an asset must not be handed out, or null when they may be. An infected file is
 * refused for every type. Gated types (archives, Office, PDF) also need a verdict first; other
 * types stay readable while `pending` or `unscannable`, since their viewers are sandboxed.
 */
export function scanBlock(scanStatus, mimeType) {
    if (scanStatus === 'infected')
        return 'infected';
    if (!isScanGatedContentType(mimeType))
        return null;
    if (scanStatus === 'pending')
        return 'pending';
    if (scanStatus === 'unscannable')
        return 'unscannable';
    return null;
}
/**
 * Uploaded types the iOS app opens in the system Quick Look viewer instead of the web viewer.
 * A subset of the upload allowlist that Quick Look renders faithfully: markdown, html, svg and
 * the text types stay in the web viewer (Quick Look would show source or run nothing useful), and
 * webm and zip are not Quick Look types. Native checks the file extension again after the match.
 */
export const NATIVE_PREVIEW_CONTENT_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/avif',
    'image/heic',
    'image/heif',
    'video/mp4',
    'video/quicktime',
    'audio/mpeg',
    'audio/mp4',
    'audio/x-m4a',
    'audio/wav',
    'audio/x-wav',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];
/** True when the iOS host can preview this content type natively (parameters and case ignored). */
export const isNativePreviewableContentType = (contentType) => NATIVE_PREVIEW_CONTENT_TYPES.includes(contentType.split(';', 1)[0]?.trim().toLowerCase() ?? '');
export const isAllowedContentType = (contentType) => UPLOAD_POLICY.allowedContentTypes.includes(contentType);
/** Size cap in bytes for an allowlisted content type; the overall ceiling for anything else. */
export const maxSizeBytesFor = (contentType) => (Object.hasOwn(MAX_SIZE_BYTES_BY_CONTENT_TYPE, contentType)
    ? MAX_SIZE_BYTES_BY_CONTENT_TYPE[contentType]
    : undefined) ?? UPLOAD_POLICY.maxFileSizeBytes;
/** Cap as a short label for messages, e.g. `20 MB`. */
export const formatSizeLimit = (bytes) => `${Math.round(bytes / MB)} MB`;
/** Extension → mime when the browser leaves `File.type` empty (common for `.md`). */
const EXTENSION_CONTENT_TYPES = {
    md: 'text/markdown',
    markdown: 'text/markdown',
    html: 'text/html',
    htm: 'text/html',
    txt: 'text/plain',
    csv: 'text/csv',
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    avif: 'image/avif',
    heic: 'image/heic',
    heif: 'image/heif',
    mp4: 'video/mp4',
    m4v: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    m4a: 'audio/mp4',
    zip: 'application/zip',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    json: 'application/json',
    yaml: 'application/yaml',
    yml: 'application/yaml',
    // Source and config files are stored as plain text: inert on every origin (served `nosniff`,
    // never executed). `.xml` is here too so it never reaches a viewer as active XML. Shell scripts,
    // `.js` and `.mjs` are blocked instead (see below).
    ...Object.fromEntries([
        'ts',
        'tsx',
        'jsx',
        'py',
        'sql',
        'go',
        'rb',
        'java',
        'kt',
        'swift',
        'rs',
        'c',
        'h',
        'cpp',
        'hpp',
        'cs',
        'php',
        'css',
        'scss',
        'xml',
        'log',
        'ini',
        'toml',
        'conf',
        'cfg',
        'jsonl',
        'ndjson',
        'diff',
        'patch',
    ].map((ext) => [ext, 'text/plain'])),
};
/**
 * Extensions refused outright, whatever type the client declares. Executables, installers,
 * launchers, and scripts a machine or a browser runs. Checked server-side at upload creation.
 */
const BLOCKED_EXTENSIONS = new Set([
    'exe',
    'dll',
    'msi',
    'dmg',
    'pkg',
    'app',
    'bat',
    'cmd',
    'com',
    'scr',
    'ps1',
    'vbs',
    'js',
    'mjs',
    'cjs',
    'jar',
    'apk',
    'deb',
    'rpm',
    // Shell scripts: Git for Windows makes `.sh` and `.bash` run on double-click through Git Bash.
    'sh',
    'bash',
    'zsh',
    // Windows script hosts, launchers, shortcuts and installers. Any of these can be uploaded as
    // text/plain and keeps its name on download, where a double-click runs it.
    'hta',
    'wsf',
    'wsh',
    'vbe',
    'jse',
    'lnk',
    'url',
    'reg',
    'cpl',
    'pif',
    'inf',
    'scf',
    'gadget',
    'application',
    'xbap',
    'msix',
    'msixbundle',
    'appx',
    'appxbundle',
    'msp',
    'mst',
    'psm1',
    'psd1',
    // Disk and virtual disk images mount on double-click.
    'iso',
    'img',
    'vhd',
    'vhdx',
    // macOS launchers.
    'command',
    'workflow',
]);
/** Lower-cased extension of a file name, ignoring trailing dots and spaces (Windows strips them). */
const extensionOf = (fileName) => {
    const trimmed = fileName.trim().replace(/[. ]+$/, '');
    return trimmed.includes('.') ? trimmed.slice(trimmed.lastIndexOf('.') + 1).toLowerCase() : '';
};
/** True when the file name ends in an extension we never accept (executables and scripts). */
export const isBlockedFileName = (fileName) => BLOCKED_EXTENSIONS.has(extensionOf(fileName));
/**
 * Content type to send on `POST /api/files/uploads`.
 * Prefers a non-empty browser MIME when it is allowlisted; otherwise guesses from the
 * file name so `.md` (often reported as `""`) does not fall through to octet-stream.
 */
export const resolveUploadContentType = (fileName, reportedType) => {
    const reported = reportedType?.trim() ?? '';
    if (reported && isAllowedContentType(reported))
        return reported;
    const ext = extensionOf(fileName);
    const guessed = ext ? (EXTENSION_CONTENT_TYPES[ext] ?? null) : null;
    if (guessed && isAllowedContentType(guessed))
        return guessed;
    return reported || 'application/octet-stream';
};
export const partCountFor = (sizeBytes) => Math.max(1, Math.ceil(sizeBytes / UPLOAD_POLICY.partSizeBytes));
/**
 * Part URLs are signed once at upload start and the clients PUT parts one after another, so the
 * window grows with the part count: the base TTL per 4 parts, capped at a day.
 */
export const presignTtlSecondsFor = (partCount) => Math.min(UPLOAD_POLICY.presignTtlSeconds * Math.max(1, Math.ceil(partCount / 4)), MAX_PRESIGN_TTL_SECONDS);
//# sourceMappingURL=policy.js.map